import { cached, tracked } from '@glimmer/tracking';

import type { DSData } from 'webapp-types';
import {
	BlockVisualization,
	ChartLegendPosition,
	DataSelectType,
	ShownLabels,
} from 'webapp/enums/enums';
import { hashCode } from 'webapp/utils/number';

import type { AnyVisualization } from 'webapp/models-custom/block-setup';
import type DataSelect from 'webapp/models-custom/data-select';
import DsDataOutputOtherLevel from 'webapp/models-custom/ds-data/output/other';
import type DsDataOutputTopLevel from 'webapp/models-custom/ds-data/output/top';
import VisualizationBase from 'webapp/models-custom/visualization/base';
import type DChartPrimaryPoint from 'webapp/d-chart/point/primary-point';
import type { SeriesData } from 'webapp/d-chart/series/series';
import DChartSeries from 'webapp/d-chart/series/series';

const RecordLimit = 225;

export default class VisualizationBubble extends VisualizationBase {
	visualizationType = BlockVisualization.BUBBLE;

	recordLimit = RecordLimit;

	get allowOthers() {
		return !!this.block.showOther;
	}

	get showValuesAsLabels() {
		return this.blockSetup.firstShownDataSelect?.shownLabels === ShownLabels.VALUE;
	}

	get showNamesAsLabels() {
		return this.blockSetup.firstShownDataSelect?.shownLabels === ShownLabels.NAME;
	}

	get showValues() {
		return this.showValuesAsLabels || this.showNamesAsLabels;
	}

	override get enabledShownLabelsOptions() {
		return [
			{
				value: ShownLabels.VALUE,
				label: 'Value',
			},
			{
				value: ShownLabels.NAME,
				label: 'Name',
			},
		];
	}

	get isLegendOff(): boolean {
		return this.block.legendPosition === ChartLegendPosition.OFF;
	}

	get isLegendRight(): boolean {
		return this.block.legendPosition === ChartLegendPosition.RIGHT;
	}

	get isLegendBottom(): boolean {
		return !this.isLegendOff && !this.isLegendRight;
	}

	get isSeriesDimensions(): boolean {
		// one series = one legend entry & one color
		// if >1 data-select, we color and group by metric (all dimensions and units of the same metric will be same color)
		// if 0-1 data-select, we color and group by dimension (units mean more of same color bubbles) unless ofc ds has color chosen
		return this.blockSetup.dataSelects.length <= 1;
	}

	override get canChangeColorsForSpecificAttributes() {
		return this.isSeriesDimensions;
	}

	// ------ Data ------

	@tracked series: DChartSeries[] = [];

	override updateDataCache() {
		this.series = this.getSeries();
		this.updateColorIndexes();
	}

	override clearDataCache() {
		this.series = [];
	}

	updateColorIndexes() {
		this.updateColorIndexesForItems(this.series);
	}

	@cached
	private get outputsByMetric() {
		type Output = DsDataOutputTopLevel | DsDataOutputOtherLevel;
		// top level = metrics (=ds)
		// second level = by attribute
		// third level = different units for this metric + attribute combo
		const outputGroups: { ds: DataSelect; outputs: Output[][] }[] = [];
		this.blockSetup.sortedDataSelectsShown.forEach((ds) => {
			const outputGroupsByDimensionValues: {
				dimensionValues: DSData.DimensionsAndValuesMap;
				outputs: Output[];
			}[] = [];

			ds.outputs.forEach((output) => {
				const existingGroup = outputGroupsByDimensionValues.find((item) =>
					output.equalDimensionValues(item.dimensionValues),
				);
				if (existingGroup) {
					existingGroup.outputs.push(output);
				} else {
					outputGroupsByDimensionValues.push({
						dimensionValues: output.dimensionValues,
						outputs: [output],
					});
				}
			});

			if (this.allowOthers) {
				const otherOutputs = ds.dataRoot?.outputsOther || [];
				if (otherOutputs.length) {
					outputGroupsByDimensionValues.push({
						dimensionValues: { other: 'Other' },
						outputs: otherOutputs,
					});
				}
			}

			const outputs = outputGroupsByDimensionValues.map(
				(item: { outputs: Output[] }) => item.outputs,
			);
			if (outputs.length) {
				outputGroups.push({ ds, outputs });
			}
		});
		return outputGroups;
	}

	private getSeries() {
		const series: DChartSeries[] = [],
			isSeriesDimensions = this.isSeriesDimensions;

		if (isSeriesDimensions) {
			// only one metric, so only one array entry
			const outputsPerAttribute = this.outputsByMetric[0]?.outputs || [],
				isSingle = outputsPerAttribute.length === 1;
			outputsPerAttribute.forEach((outputsForAttribute) => {
				const firstOutput = outputsForAttribute[0]!,
					isOther = firstOutput instanceof DsDataOutputOtherLevel,
					points: DChartPrimaryPoint[] = [];

				outputsForAttribute.forEach((output) => {
					const point = this.createDChartPointFromPoint(
						output,
						(output as DsDataOutputTopLevel).point ||
							(output as DsDataOutputOtherLevel).pointTotal,
					);
					points.push(point);
				});

				const metricSelectionData = Object.assign(
					{},
					firstOutput?.metricSelectionData || {},
				);
				// metricSelectionData.attribute = ''; // to match correct attribute colors

				let title = isOther ? 'Other' : firstOutput.attributeNamesString;
				if (isSingle && !title) {
					title = metricSelectionData.metricName;
				}
				const seriesData: SeriesData = {
					type: DataSelectType.MAIN,
					timezone: this.dataTimezone,
					title,
					points,
					showDataLabels: this.showValues,
					metricSelectionData,
				};

				const serie = new DChartSeries(seriesData);
				series.push(serie);
			});
		} else {
			this.outputsByMetric.forEach((outputData) => {
				const points: DChartPrimaryPoint[] = [],
					ds = outputData.ds;
				outputData.outputs.forEach((outputs) => {
					outputs.forEach((output) => {
						const point = this.createDChartPointFromPoint(
							output,
							(output as DsDataOutputTopLevel).point ||
								(output as DsDataOutputOtherLevel).pointTotal,
						);
						points.push(point);
					});
				});

				const seriesData: SeriesData = {
					type: DataSelectType.MAIN,
					timezone: this.dataTimezone,
					title: ds.shownTitle,
					points,
					showDataLabels: this.showValues,
					metricSelectionData: {
						dsId: ds.numericId!,
						primaryDsId: null,
						metricSetupData: ds.metricSetup.metricSetupData,
						metricName: ds.data?.metricName || '',
						dimensionValues: {}, // bcos different
						unit: '', // bcos different
					},
				};

				const serie = new DChartSeries(seriesData);
				series.push(serie);
			});
		}

		return series;
	}

	// ----------- Detecting possible rendering changes -----------

	override get visualizationSettingsHash(): number {
		const block = this.block;
		return hashCode(
			JSON.stringify({
				lPos: block.legendPosition,
				sv1: this.showValuesAsLabels,
				sv2: this.showNamesAsLabels,
				bc: block.colors,
				o: this.allowOthers,
			}),
		);
	}

	// ------- visualization switch helpers --------

	override preCleanupForSwitchTo(visualizationTo: AnyVisualization): void {
		super.preCleanupForSwitchTo(visualizationTo);

		// cleanup shownLabels.NAME
		this.blockSetup.dataSelectsShown.forEach((ds) => {
			if (ds.shownLabels === ShownLabels.NAME) {
				ds.shownLabels = ShownLabels.VALUE;
			}
		});
		this.loggerService.logToConsole('[Vis change] from Bubble: remove showLabels: name');
	}

	// ------- MISC --------

	override getAllMainPoints() {
		const points: DChartPrimaryPoint[] = [];

		this.series.forEach((serie) => {
			points.push(...serie.points);
		});

		return points;
	}

	// ------- general helpers --------

	override onCreatePrimaryMainDataSelect(dataSelect: DataSelect): void {
		super.onCreatePrimaryMainDataSelect(dataSelect);

		// in case we went from isSeriesDimensions, we need to clear the attribute colors
		if (!this.canChangeColorsForSpecificAttributes) {
			this.blockSetup.checkBlockColorValidity(true);
		}
	}

	static override recordLimit = RecordLimit;
}
